/*
 * Amara PDP — progressive enhancement only.
 *
 * Commerce behaviour (add to cart, variant selection, cart drawer, wallet
 * buttons) stays owned by the theme's product-form.js / variant-picker.js and
 * Shopify's native payment button. Nothing here re-implements checkout logic.
 *
 * Responsibilities:
 *   1. Gallery — scroll-snap sync, thumbnails, keyboard nav, counter, lightbox.
 *   2. Sticky add to cart — reveal once the primary CTA leaves the viewport.
 *   3. Variant change — keep gallery, sticky price and sticky variant id in sync.
 */
(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-amara-product]';
  const READY_FLAG = 'amaraProductReady';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- gallery */

  class AmaraGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-gallery-track]');
      if (!this.track) return;

      this.slides = Array.from(root.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
      this.dots = Array.from(root.querySelectorAll('[data-gallery-dot]'));
      this.currentLabel = root.querySelector('[data-gallery-current]');
      this.index = 0;
      this.visibleIndices = this.slides.map((_, i) => i);
      const mapNode = root.querySelector('[data-variant-gallery-map]');
      if (mapNode) {
        try {
          this.variantGalleryMap = JSON.parse(mapNode.textContent);
          this.filterVariant(root.dataset.initialVariantId);
        } catch (error) {
          console.warn('Amara gallery map error', error);
        }
      }

      this.bindTrack();
      this.bindThumbs();
      this.bindKeyboard();
      this.bindLightbox();
    }

    filterVariant(variantId) {
      const ids = this.variantGalleryMap?.[String(variantId)];
      if (!ids) return;
      const allowed = new Set(String(ids).split('|').filter(Boolean));
      const visible = [];
      this.slides.forEach((slide, i) => {
        const show = allowed.has(slide.dataset.mediaId);
        slide.style.display = show ? '' : 'none';
        slide.setAttribute('aria-hidden', String(!show));
        if (show) visible.push(i);
      });
      this.thumbs.forEach((node, i) => { node.style.display = allowed.has(this.slides[i]?.dataset.mediaId) ? '' : 'none'; });
      this.dots.forEach((node, i) => { node.style.display = allowed.has(this.slides[i]?.dataset.mediaId) ? '' : 'none'; });
      this.visibleIndices = visible;
      if (!visible.length) return;
      this.index = visible[0];
      this.setActive(this.index, false);
      if (this.track) this.track.scrollLeft = 0;
      const total = this.root.querySelector('[data-gallery-counter] span:last-child');
      if (total) total.textContent = String(visible.length);
      const lightboxTotal = this.dialog?.querySelector('.amara-lightbox__counter');
      if (lightboxTotal) {
        const last = lightboxTotal.lastChild;
        if (last?.nodeType === Node.TEXT_NODE) last.textContent = ' / ' + visible.length;
      }
    }

    bindTrack() {
      if (this.slides.length < 2) return;

      // IntersectionObserver on the scroll container beats a scroll listener:
      // it survives momentum scrolling on iOS without rAF throttling.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio >= 0.55) {
              this.setActive(Number(entry.target.dataset.index), false);
            }
          });
        },
        { root: this.track, threshold: [0.55] }
      );

      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    bindThumbs() {
      const go = (event) => {
        const button = event.target.closest('[data-gallery-thumb]');
        if (!button) return;
        const target = Number(button.dataset.index);
        if (this.visibleIndices.includes(target)) this.scrollTo(this.visibleIndices.indexOf(target));
      };

      const thumbsWrap = this.root.querySelector('[data-gallery-thumbs]');
      if (thumbsWrap) thumbsWrap.addEventListener('click', go);
    }

    bindKeyboard() {
      if (this.slides.length < 2) return;

      this.track.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        const currentPosition = this.visibleIndices.indexOf(this.index);
        this.scrollTo((currentPosition < 0 ? 0 : currentPosition) + delta);
      });
    }

    setActive(index, scroll = true) {
      if (Number.isNaN(index) || index < 0 || index >= this.slides.length || !this.visibleIndices.includes(index)) return;

      this.index = index;

      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));

      if (this.currentLabel) this.currentLabel.textContent = String(this.visibleIndices.indexOf(index) + 1);
      if (scroll) this.scrollTo(index);
    }

    scrollTo(index) {
      if (this.visibleIndices?.length && this.visibleIndices.length < this.slides.length) {
        index = this.visibleIndices[((index % this.visibleIndices.length) + this.visibleIndices.length) % this.visibleIndices.length];
      } else {
        if (index < 0) index = this.slides.length - 1;
        if (index >= this.slides.length) index = 0;
      }

      const slide = this.slides[index];
      if (!slide) return;

      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    /* Shows the media belonging to a variant, when the variant has its own image. */
    showMediaId(mediaId) {
      if (!mediaId) return;
      const slide = this.slides.find((item) => item.dataset.mediaId === String(mediaId));
      if (slide) this.scrollTo(this.visibleIndices.indexOf(Number(slide.dataset.index)));
    }

    /* ------------------------------------------------------------ lightbox */

    bindLightbox() {
      this.dialog = this.root.querySelector('[data-gallery-lightbox]');
      const trigger = this.root.querySelector('[data-gallery-zoom]');
      const dataNode = this.root.querySelector('[data-gallery-zoom-data]');

      if (!this.dialog || !dataNode || typeof this.dialog.showModal !== 'function') {
        if (trigger) trigger.hidden = true;
        return;
      }

      try {
        this.zoomImages = JSON.parse(dataNode.textContent);
      } catch (error) {
        console.warn('Amara PDP: could not parse zoom data.', error);
        if (trigger) trigger.hidden = true;
        return;
      }

      this.lightboxImage = this.dialog.querySelector('[data-lightbox-image]');
      this.lightboxCounter = this.dialog.querySelector('[data-lightbox-current]');

      if (trigger) trigger.addEventListener('click', () => this.openLightbox(this.index));

      this.track.addEventListener('click', (event) => {
        // Never hijack a click on a video control or model viewer.
        if (event.target.closest('video, model-viewer, iframe')) return;

        // On the desktop grid every slide is on screen at once, so open the
        // image that was actually clicked rather than the carousel's index.
        const slide = event.target.closest('[data-gallery-slide]');
        this.openLightbox(slide ? Number(slide.dataset.index) : this.index);
      });

      this.dialog
        .querySelector('[data-lightbox-close]')
        ?.addEventListener('click', () => this.dialog.close());

      this.dialog
        .querySelector('[data-lightbox-prev]')
        ?.addEventListener('click', () => this.stepLightbox(-1));

      this.dialog
        .querySelector('[data-lightbox-next]')
        ?.addEventListener('click', () => this.stepLightbox(1));

      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.stepLightbox(1);
        if (event.key === 'ArrowLeft') this.stepLightbox(-1);
      });

      // Click the backdrop (i.e. the dialog element itself) to dismiss.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      this.dialog.addEventListener('close', () => {
        this.setActive(this.lightboxIndex ?? this.index);
      });
    }

    openLightbox(index) {
      if (!this.paintLightbox(index)) return;
      this.dialog.showModal();
    }

    stepLightbox(delta) {
      const available = (this.visibleIndices || []).filter(i => this.zoomImages[i]);
      if (!available.length) return;
      const position = available.indexOf(this.lightboxIndex ?? this.index);
      const nextIndex = available[((position < 0 ? 0 : position) + delta + available.length) % available.length];
      this.paintLightbox(nextIndex);
      return;
      let next = (this.lightboxIndex ?? 0) + delta;
      const total = this.zoomImages.length;

      // Skip past video/model entries, which have no zoomable source.
      for (let attempts = 0; attempts < total; attempts += 1) {
        if (next < 0) next = total - 1;
        if (next >= total) next = 0;
        if (this.zoomImages[next]) break;
        next += delta || 1;
      }

      this.paintLightbox(next);
    }

    paintLightbox(index) {
      if (!this.visibleIndices.includes(index)) return false;
      const entry = this.zoomImages?.[index];
      if (!entry) return false;

      this.lightboxIndex = index;
      this.lightboxImage.src = entry.src;
      this.lightboxImage.alt = entry.alt || '';
      if (this.lightboxCounter) this.lightboxCounter.textContent = String(this.visibleIndices.indexOf(index) + 1);
      return true;
    }
  }

  /* ------------------------------------------------------------ sticky ATC */

  function initStickyAtc(section) {
    const sticky = section.querySelector('[data-amara-sticky]');
    const anchor = section.querySelector('[data-amara-atc-anchor]');
    if (!sticky || !anchor) return null;

    sticky.hidden = false;

    const setVisible = (visible) => {
      sticky.classList.toggle('is-visible', visible);
      document.body.classList.toggle('amara-sticky-visible', visible);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only reveal once the CTA has scrolled off the TOP of the viewport,
        // so the bar never covers a CTA the customer has not reached yet.
        const scrolledPast = entry.boundingClientRect.top < 0;
        setVisible(!entry.isIntersecting && scrolledPast);
      },
      { threshold: 0 }
    );

    observer.observe(anchor);
    return sticky;
  }

  /* ----------------------------------------------------------- description */

  function initDetails(section) {
    // Nothing to wire up: <details> is native. Kept as a hook point so the
    // markup contract stays visible from the JS side.
    return section.querySelectorAll('[data-amara-details]').length;
  }

  /* ------------------------------------------------------------------ init */

  function init(section) {
    if (!section || section.dataset[READY_FLAG] === 'true') return;
    section.dataset[READY_FLAG] = 'true';

    const galleryRoot = section.querySelector('[data-amara-gallery]');
    const gallery = galleryRoot ? new AmaraGallery(galleryRoot) : null;
    const sticky = initStickyAtc(section);

    initDetails(section);

    const productId = section.dataset.productId;
    if (!productId || !window.MinimogEvents?.subscribe) return;

    window.MinimogEvents.subscribe(`${productId}__VARIANT_CHANGE`, (variant) => {
      if (!variant) return;

      section.classList.toggle('is-unavailable', !variant.available);

      if (gallery?.variantGalleryMap) gallery.filterVariant(variant.id);
      if (gallery && variant.featured_media?.id) {
        gallery.showMediaId(variant.featured_media.id);
      }

      if (!sticky) return;

      // variant-picker.js only rewrites #product-form-<section> and
      // #product-form-installment, so the sticky form is ours to keep current.
      const stickyId = sticky.querySelector('[name="id"]');
      if (stickyId) stickyId.value = variant.id;

      const stickyPrice = sticky.querySelector('[data-amara-sticky-price]');
      const formatMoney = window.Shopify?.formatMoney;
      if (stickyPrice && typeof formatMoney === 'function') {
        stickyPrice.innerHTML = formatMoney(
          variant.price,
          window.MinimogSettings?.money_format
        );
      }
    });
  }

  function scan(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) init(root);
    root.querySelectorAll?.(SECTION_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
